/**
 * Hook: useAiContext
 *
 * Manages AI context awareness for the IDE:
 * 1. Indexes workspace files into AI vector memory when workspace loads
 * 2. Provides a function to gather current project context for AI operations
 * 3. Re-indexes when files change significantly
 */

import { useEffect, useCallback, useRef } from 'react';
import { useEditorStore } from '@/store/editorStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import * as aiService from '@/services/ai.service';
import * as fileService from '@/services/file.service';
import type { WorkspaceFile } from '@/types';

/**
 * Flatten a file tree into a Record<path, content> map.
 * Reads actual content for each file from the workspace container.
 */
async function gatherProjectFiles(
  workspaceId: string,
  tree: WorkspaceFile | null,
): Promise<Record<string, string>> {
  if (!tree?.children) return {};

  const files: Record<string, string> = {};
  const solFiles: string[] = [];

  // Collect all Solidity files from the tree
  function walk(node: WorkspaceFile) {
    if (node.type === 'file' && (node.path.endsWith('.sol') || node.path.endsWith('.t.sol'))) {
      solFiles.push(node.path);
    }
    if (node.children) {
      node.children.forEach(walk);
    }
  }
  walk(tree);

  // Fetch content for up to 20 files (to avoid overwhelming the AI)
  const toFetch = solFiles.slice(0, 20);
  await Promise.allSettled(
    toFetch.map(async (path) => {
      try {
        const content = await fileService.getFileContent(workspaceId, path);
        files[path] = content;
      } catch { /* skip files that fail to read */ }
    }),
  );

  return files;
}

export function useAiContext() {
  const { activeWorkspace } = useWorkspaceStore();
  const { fileTree, openFiles, tabs, activeTabId } = useEditorStore();
  const indexedRef = useRef<string | null>(null);

  // Index workspace files into AI memory when workspace loads
  useEffect(() => {
    if (!activeWorkspace || activeWorkspace.status !== 'running') return;
    if (indexedRef.current === activeWorkspace.id) return; // Already indexed
    if (!fileTree) return;

    indexedRef.current = activeWorkspace.id;

    // Index in background — don't block UI
    gatherProjectFiles(activeWorkspace.id, fileTree).then((files) => {
      if (Object.keys(files).length > 0) {
        aiService.indexMemory(activeWorkspace.id, files).catch(() => {
          // Memory indexing is best-effort — don't fail the IDE
        });
      }
    });
  }, [activeWorkspace, fileTree]);

  /**
   * Get current AI context including open file + project files.
   * Used by AI panel, code actions, and the fix loop.
   */
  const getAiContext = useCallback((): Record<string, unknown> => {
    const tab = tabs.find((t) => t.id === activeTabId);
    if (!tab) return {};

    const content = openFiles.get(tab.path);
    if (!content) return {};

    // Also gather content from other open tabs for multi-file context
    const otherFiles: Record<string, string> = {};
    for (const t of tabs) {
      if (t.id !== activeTabId) {
        const c = openFiles.get(t.path);
        if (c) otherFiles[t.path] = c.slice(0, 5000);
      }
    }

    return {
      fileName: tab.path,
      language: tab.language,
      code: content.slice(0, 12000),
      otherFiles,
      workspaceId: activeWorkspace?.id,
    };
  }, [activeTabId, openFiles, tabs, activeWorkspace]);

  return { getAiContext };
}
